import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Eye, History, Loader2, Save, TriangleAlert } from 'lucide-react';
import client, { apiError } from './adminApi';
import { RESOURCES } from './resources';
import { Field, getPath } from './components/fields';
import { ConfirmDialog, SeoWarnings, StatusBadge } from './components/ui';
import { AiSeoPanel } from './components/AiSeoPanel';
import { AiDraftPanel } from './components/AiDraftPanel';
import { useAdminAuth } from './AdminAuth';

// Pre-publish checklist for Insights articles — the fields the public site
// needs. Informational, never blocking: the author can publish with gaps and
// the site renders fallbacks, but nothing is silently broken.
const publishChecklist = (doc) => [
  { key: 'title', label: 'Title', ok: !!(doc.title || '').trim() },
  { key: 'excerpt', label: 'Excerpt / summary', ok: !!(doc.excerpt || '').trim() },
  { key: 'topic', label: 'Primary topic', ok: !!doc.primaryTopic },
  { key: 'image', label: 'Featured image', ok: !!(doc.image && doc.image.src) },
  { key: 'date', label: 'Publish date', ok: !!(doc.publishedAt || doc.scheduledFor) },
];

// Generic record editor: field-schema-driven form, workflow actions,
// version history + restore, secure draft preview, unsaved-changes guard,
// server-confirmed success only.
export default function ResourceEditPage() {
  const { resource, id } = useParams();
  const isNew = id === undefined;
  const cfg = RESOURCES[resource];
  const { user } = useAdminAuth();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [savedDoc, setSavedDoc] = useState(null);
  const [seoWarn, setSeoWarn] = useState([]);
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null); // {action, title, message, destructive}
  const dirtyRef = useRef(false);

  const load = useCallback(async () => {
    if (isNew) {
      setDoc({ status: 'draft' });
      setSavedDoc(JSON.stringify({ status: 'draft' }));
      return;
    }
    try {
      const res = await client.get(`/admin/content/${resource}/${id}`);
      setDoc(res.data.item);
      setSavedDoc(JSON.stringify(res.data.item));
      setSeoWarn(res.data.item.seoWarnings || res.data.seoWarnings || []);
      const v = await client.get(`/admin/content/${resource}/${id}/versions`);
      setVersions(v.data.versions);
    } catch (err) {
      setError(apiError(err, 'Record could not be loaded.'));
    }
  }, [resource, id, isNew]);

  useEffect(() => { load(); }, [load]);

  // Unsaved-changes guard (browser/tab close)
  useEffect(() => {
    const handler = (e) => {
      if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  if (!cfg) return <p className="text-sm text-red-700">Unknown content type.</p>;
  if (error && !doc) return <p role="alert" className="text-sm font-semibold text-red-700">{error}</p>;
  if (!doc) return <div className="flex justify-center py-16" role="status"><Loader2 className="h-6 w-6 animate-spin text-navy-900" /></div>;

  const canPublish = ['owner', 'administrator'].includes(user.role);
  const canWrite = cfg.rolesWrite.includes(user.role);
  const dirty = savedDoc !== null && JSON.stringify(doc) !== savedDoc;
  dirtyRef.current = dirty;

  const onChange = (next) => { setDoc(next); setNotice(''); };

  // Publishing with checklist gaps asks once, then proceeds — never blocks.
  const attemptPublish = () => {
    if (resource !== 'articles') return save('published');
    const missing = publishChecklist(doc).filter((c) => !c.ok);
    if (!missing.length) return save('published');
    setConfirm({
      action: 'publish-anyway',
      title: 'Publish with missing items?',
      message: `Missing: ${missing.map((m) => m.label).join(', ')}. The public site will show fallback placeholders for anything missing. You can publish anyway and complete it later.`,
      destructive: false,
      confirmLabel: 'Publish Anyway',
    });
    return null;
  };

  const save = async (targetStatus) => {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const payload = { ...doc };
      if (targetStatus) payload.status = targetStatus;
      let savedId = id;
      if (isNew) {
        const res = await client.post(`/admin/content/${resource}`, payload);
        savedId = getPath(res.data.item, cfg.idField);
        setSeoWarn(res.data.seoWarnings || []);
        setNotice('Saved.');
        setDoc(res.data.item);
        setSavedDoc(JSON.stringify(res.data.item));
        navigate(`/admin/content/${resource}/${savedId}/edit`, { replace: true });
      } else {
        const res = await client.put(`/admin/content/${resource}/${id}`, payload);
        setSeoWarn(res.data.seoWarnings || []);
        setDoc(res.data.item);
        setSavedDoc(JSON.stringify(res.data.item));
        setNotice(targetStatus === 'published' ? 'Published — the change is now live.' : 'Saved.');
        const v = await client.get(`/admin/content/${resource}/${getPath(res.data.item, cfg.idField)}/versions`);
        setVersions(v.data.versions);
      }
    } catch (err) {
      setError(apiError(err, 'The record could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  const doWorkflow = async (action) => {
    setBusy(true);
    setError('');
    try {
      await client.post(`/admin/content/${resource}/${id}/${action}`);
      await load();
      setNotice(action === 'publish' ? 'Published — the change is now live.' : 'Archived.');
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const doDelete = async () => {
    try {
      await client.delete(`/admin/content/${resource}/${id}`, { params: { confirm: true } });
      navigate(`/admin/content/${resource}`);
    } catch (err) {
      setError(apiError(err));
      setConfirm(null);
    }
  };

  const doRestore = async (version) => {
    try {
      await client.post(`/admin/content/${resource}/${id}/restore/${version}`);
      await load();
      setNotice(`Restored version ${version}. Review and save to publish.`);
    } catch (err) {
      setError(apiError(err));
    }
  };

  const doPreview = async () => {
    // Open the tab synchronously (inside the click gesture) — awaiting the
    // token first loses the gesture and browsers silently block the popup.
    const win = window.open('about:blank', '_blank');
    try {
      setError('');
      const recordId = getPath(doc, cfg.idField);
      const res = await client.post('/public/preview-token', { resource, slug: recordId });
      const url = new URL(`/preview/${resource}/${recordId}?token=${res.data.token}`, window.location.origin).href;
      if (win) {
        win.location.href = url;
      } else {
        // Popup fully blocked — navigate this tab instead of doing nothing.
        window.location.href = url;
      }
    } catch (err) {
      if (win) win.close();
      setError(apiError(err, 'Preview is only available after the record has been saved once.'));
    }
  };

  return (
    <div data-testid={`admin-edit-${resource}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to={`/admin/content/${resource}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-navy-900 hover:underline" data-testid="admin-back">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> {cfg.label}
          </Link>
          <span className="text-slate-300" aria-hidden="true">/</span>
          <h1 className="text-xl font-extrabold tracking-tight text-navy-900">
            {isNew ? `New ${cfg.singular}` : getPath(doc, cfg.labelField) || 'Edit'}
          </h1>
          {!cfg.noWorkflow && !isNew && <StatusBadge status={doc.status} />}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isNew && (
            <button type="button" onClick={doPreview} data-testid="admin-preview" className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3.5 py-2 text-xs font-bold text-navy-900 hover:bg-navy-50">
              <Eye className="h-3.5 w-3.5" aria-hidden="true" /> Preview
            </button>
          )}
          {canWrite && (
            <button type="button" onClick={() => save()} disabled={busy} data-testid="admin-save" className="inline-flex items-center gap-1.5 rounded-md bg-navy-900 px-4 py-2 text-xs font-bold text-white hover:bg-navy-700 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Save className="h-3.5 w-3.5" aria-hidden="true" />}
              Save
            </button>
          )}
          {canPublish && doc.status !== 'published' && (
            <button type="button" onClick={attemptPublish} disabled={busy} data-testid="admin-publish" className="rounded-md bg-gold-600 px-4 py-2 text-xs font-bold text-navy-950 hover:bg-gold-500 disabled:opacity-50">
              Publish
            </button>
          )}
          {!canPublish && (
            <button type="button" onClick={() => save('in_review')} disabled={busy} data-testid="admin-submit-review" className="rounded-md border-2 border-navy-900 px-4 py-1.5 text-xs font-bold text-navy-900 hover:bg-navy-50 disabled:opacity-50">
              Submit for Review
            </button>
          )}
        </div>
      </div>

      {notice && <p role="status" data-testid="admin-notice" className="mt-4 rounded-md border border-teal-200 bg-teal-50 px-4 py-2.5 text-sm font-semibold text-teal-800">{notice}</p>}
      {error && <p role="alert" data-testid="admin-error" className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700">{error}</p>}

      <div className="mt-6 grid gap-6 xl:grid-cols-[1fr_320px]">
        <div className="space-y-5 rounded-xl border border-slate-200 bg-white p-6">
          {cfg.fields.map((f) => (
            <Field key={f.key} field={f} doc={doc} onChange={canWrite ? onChange : () => {}} />
          ))}
        </div>

        <div className="space-y-4">
          {resource === 'articles' && canWrite && (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="publish-checklist">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Publish checklist</p>
              <ul className="mt-3 space-y-1.5">
                {publishChecklist(doc).map((c) => (
                  <li key={c.key} className="flex items-center gap-2 text-xs font-semibold" data-testid={`checklist-${c.key}`}>
                    {c.ok
                      ? <Check className="h-3.5 w-3.5 text-teal-600" aria-hidden="true" />
                      : <TriangleAlert className="h-3.5 w-3.5 text-amber-500" aria-hidden="true" />}
                    <span className={c.ok ? 'text-slate-600' : 'text-amber-700'}>{c.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <AiDraftPanel
            resource={resource}
            doc={doc}
            onApply={(key, value) => onChange({ ...doc, [key]: value })}
          />
          {cfg.fields.some((f) => f.key === 'seo') && canWrite && (
            <AiSeoPanel
              resource={resource}
              doc={doc}
              onApply={(key, value) => onChange({ ...doc, seo: { ...(doc.seo || {}), [key]: value } })}
            />
          )}
          <SeoWarnings warnings={seoWarn} />

          {!isNew && canPublish && !cfg.noWorkflow && (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Publishing</p>
              <div className="mt-3 space-y-2">
                {doc.status === 'published' && (
                  <button type="button" onClick={() => setConfirm({ action: 'archive', title: 'Archive this record?', message: 'It will be removed from the public website but preserved in the admin area. You can restore it later.', destructive: false })} data-testid="admin-archive" className="w-full rounded-md border border-slate-300 px-3 py-2 text-xs font-bold text-navy-900 hover:bg-navy-50">
                    Unpublish &amp; Archive
                  </button>
                )}
                <button type="button" onClick={() => setConfirm({ action: 'delete', title: 'Permanently delete?', message: 'This cannot be undone easily. Archiving is usually safer. A snapshot is kept in version history.', destructive: true })} data-testid="admin-delete" className="w-full rounded-md border border-red-200 px-3 py-2 text-xs font-bold text-red-700 hover:bg-red-50">
                  Delete Permanently
                </button>
              </div>
            </div>
          )}

          {!isNew && versions.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="admin-versions">
              <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                <History className="h-3.5 w-3.5" aria-hidden="true" /> Version history
              </p>
              <ul className="mt-3 max-h-64 space-y-2 overflow-y-auto">
                {versions.map((v) => (
                  <li key={v.versionId} className="flex items-center justify-between gap-2 text-xs">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-navy-900">v{v.version} — {v.summary}</p>
                      <p className="text-slate-400">{v.changedBy} · {new Date(v.changedAt).toLocaleString('en-IE')}</p>
                    </div>
                    {canPublish && (
                      <button type="button" onClick={() => doRestore(v.version)} data-testid={`restore-v${v.version}`} className="shrink-0 rounded border border-slate-300 px-2 py-1 font-bold text-navy-900 hover:bg-navy-50">
                        Restore
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!isNew && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-xs text-slate-500">
              <p className="font-bold uppercase tracking-[0.14em] text-slate-400">Record</p>
              <dl className="mt-2 space-y-1">
                <div className="flex justify-between"><dt>Created by</dt><dd className="font-semibold text-navy-900">{doc.createdBy || '—'}</dd></div>
                <div className="flex justify-between"><dt>Updated by</dt><dd className="font-semibold text-navy-900">{doc.updatedBy || '—'}</dd></div>
                {doc.publishedBy && <div className="flex justify-between"><dt>Published by</dt><dd className="font-semibold text-navy-900">{doc.publishedBy}</dd></div>}
              </dl>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        destructive={confirm?.destructive}
        confirmLabel={confirm?.confirmLabel || (confirm?.action === 'delete' ? 'Delete Permanently' : 'Confirm')}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm?.action === 'delete') return doDelete();
          if (confirm?.action === 'publish-anyway') { setConfirm(null); return save('published'); }
          return doWorkflow(confirm?.action);
        }}
      />
    </div>
  );
}
